import { computed, inject, Injectable } from '@angular/core';

import {
  getDefaultStepDescription,
  REGISTRATION_FINISHED_PROGRESS_STEP,
  REGISTRATION_PENDING_BY_ADMIN_PROGRESS_STEP,
  REGISTRATION_STEP_DEFINITIONS,
} from '@app/features/registration/constants/registration-steps.const';
import { RegistrationProgressStepView } from '@app/features/registration/types/registration-progress-step.view';
import { RegistrationSectionStatus } from '@app/features/registration/types';

import { RegistrationNavigationService } from './registration-navigation.service';
import { RegistrationStore } from './registration-store.service';
import { RegistrationWizardService } from './registration-wizard.service';

/**
 * Builds the read-only view models the wizard chrome renders: the progress
 * steps, the steps container width, and the layout visibility flags.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationProgressPresenter {
  private readonly store = inject(RegistrationStore);
  private readonly wizard = inject(RegistrationWizardService);
  private readonly navigation = inject(RegistrationNavigationService);

  readonly showReviewPanel = computed(() => !this.navigation.isPendingRoute());

  readonly showSingleIdentityStep = computed(
    () => this.store.isPendingByAdmin() || this.store.isFinished(),
  );

  readonly progressSteps = computed((): RegistrationProgressStepView[] => {
    if (this.store.isPendingByAdmin()) {
      return [REGISTRATION_PENDING_BY_ADMIN_PROGRESS_STEP];
    }

    if (this.store.isFinished()) {
      return [REGISTRATION_FINISHED_PROGRESS_STEP];
    }

    const routes = this.wizard.stepRoutes();
    const currentIndex = this.navigation.wizardStepIndex();

    return routes.map((route, index) => {
      const definition = REGISTRATION_STEP_DEFINITIONS[route];
      const sectionStatus = this.store.getSectionByRoute(route)?.status;

      if (index === currentIndex) {
        return {
          ...definition,
          status: 'in-progress',
          description: { text: 'درحال انجام' },
          isActive: true,
          isNavigable: true,
        };
      }

      if (sectionStatus === RegistrationSectionStatus.Finished) {
        return {
          ...definition,
          status: 'done',
          description: { text: getDefaultStepDescription(index) },
          isActive: false,
          isNavigable: true,
        };
      }

      return {
        ...definition,
        status: 'default',
        description: { text: getDefaultStepDescription(index) },
        isActive: false,
        isNavigable: sectionStatus != null && this.store.isSectionNavigable(sectionStatus),
      };
    });
  });

  readonly stepsContainerWidthClass = computed(() => {
    if (this.showSingleIdentityStep()) {
      return 'w-[192px]';
    }

    return this.wizard.stepRoutes().length === 3
      ? 'w-[544px] xl:w-[577px]'
      : 'w-[720px] xl:w-[768px]';
  });
}
